import { HTMLAttributes, useMemo } from 'react';
import { HeaderGroup, Table } from '@tanstack/react-table';
import { TableHeader, TableRow } from '@sib/ui';
import { DataTableHeaderCell } from './DataTableHeaderCell';
import { DataTableSortableHeaderCell } from './DataTableSortableHeaderCell';
import { useResizeConstraints } from './DataTableHeader.utils';

interface DataTableHeaderProps<T>
  extends HTMLAttributes<HTMLTableSectionElement> {
  table: Table<T>;
  headerGroups: HeaderGroup<T>[];
  hideRounding?: boolean;
  withFilters?: boolean;
  withFixedColumnWidth?: boolean;
  reorderableColumnIds?: string[];
}

export const DataTableHeader = <T,>({
  table,
  headerGroups,
  hideRounding = false,
  withFilters,
  withFixedColumnWidth = false,
  reorderableColumnIds,
}: DataTableHeaderProps<T>) => {
  const { resizeJustEndedRef } = useResizeConstraints<T>({ table });

  const reorderableSet = useMemo(
    () => new Set(reorderableColumnIds ?? []),
    [reorderableColumnIds]
  );

  return (
    <TableHeader className="relative">
      {headerGroups.map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => {
            const isLastInRow = index === headerGroup.headers.length - 1;
            const canUserSort = header.column.columnDef.sortable ?? true;

            const onSortClick: React.MouseEventHandler<
              HTMLTableCellElement
            > = () => {
              if (resizeJustEndedRef.current)
                return (resizeJustEndedRef.current = false);

              if (!canUserSort) return;
              const sorting = header.column.getIsSorted();
              if (sorting && sorting === 'desc')
                return header.column.clearSorting();
              if (sorting && sorting === 'asc')
                return header.column.toggleSorting(true);
              header.column.toggleSorting(false);
            };

            const cellProps = {
              header,
              table,
              hideRounding,
              withFilters,
              withFixedColumnWidth,
              isLastInRow,
              onSortClick,
              key: header.id,
            };

            return reorderableSet.has(header.column.id) ? (
              <DataTableSortableHeaderCell {...cellProps} />
            ) : (
              <DataTableHeaderCell {...cellProps} />
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
};
